import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Lock, Star, Zap } from "lucide-react";

interface LockedPreviewProps {
  title: string;
  description: string;
  targetTier: "core" | "pro";
  onUpgrade: () => void;
}

export function LockedPreview({ title, description, targetTier, onUpgrade }: LockedPreviewProps) {
  const targetTierName = targetTier === "core" ? "Family" : "Master Chef";
  const targetTierColor = targetTier === "core" ? "warning" : "sage";
  const iconColorClass = targetTier === "core" ? "text-warning" : "text-sage";
  
  return (
    <div className="relative">
      {/* Blurred Content Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-muted/50 to-muted/30 backdrop-blur-sm rounded-lg z-10" />
      
      {/* Mock Content */}
      <div className="space-y-4 opacity-30">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="h-4 bg-muted rounded w-3/4" />
            <div className="h-8 bg-muted rounded" />
          </div>
          <div className="space-y-2">
            <div className="h-4 bg-muted rounded w-2/3" />
            <div className="h-8 bg-muted rounded" />
          </div>
        </div>
        <div className="space-y-2">
          <div className="h-4 bg-muted rounded w-1/2" />
          <div className="flex gap-2">
            <div className="h-8 bg-muted rounded-full w-16" />
            <div className="h-8 bg-muted rounded-full w-20" />
            <div className="h-8 bg-muted rounded-full w-18" />
          </div>
        </div>
      </div>
      
      {/* Lock Overlay */}
      <Card className={`absolute inset-0 z-20 border-2 ${
        targetTier === "core" ? "border-warning/20 bg-warning/5" : "border-sage/20 bg-sage/5"
      }`}>
        <CardContent className="h-full flex flex-col items-center justify-center text-center p-6">
          <div className={`p-3 rounded-full mb-4 ${
            targetTier === "core" ? "bg-warning/10" : "bg-sage/10"
          }`}>
            {targetTier === "core" ? (
              <Star className={`h-6 w-6 ${iconColorClass}`} />
            ) : (
              <Zap className={`h-6 w-6 ${iconColorClass}`} />
            )}
          </div>
          
          <h3 className="font-medium mb-2">{title}</h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-xs">
            {description}
          </p>
          
          <div className="flex items-center gap-2 mb-4">
            <Lock className="h-4 w-4 text-muted-foreground" />
            <Badge className={`${
              targetTier === "core" 
                ? "bg-warning/10 text-warning border-warning/20" 
                : "bg-sage/10 text-sage border-sage/20"
            }`}>
              {targetTierName}
            </Badge>
          </div>
          
          <Button
            onClick={() => {
              // Analytics would track upgrade intent here
              console.log(`Upgrade intent: ${targetTier} from Kitchen Profile`);
              onUpgrade();
            }}
            variant={targetTier === "core" ? "default" : "secondary"}
          >
            Upgrade to {targetTierName}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
